import { useEffect, useState } from 'react';
import { FeatureFlags } from '@demind-inc/core';
import { fetchAndActivate, getAll, isSupported } from 'firebase/remote-config';

import { remoteConfig, useAuthContext } from '../data-access';
import { isDevelopment } from './environment';

//@link: https://www.notion.so/demind-inc/Feature-Flags-2327fe3c821a444d9076b771c606c0be

// FF_lifestack_v4 isn't in the installed @demind-inc/core's FeatureFlags type
// yet (only FF_lifestack_v3 is) — extend it locally rather than waiting on a
// core package version bump. Safe to drop once core publishes the real field.
export type ExtendedFeatureFlags = FeatureFlags & {
  /** Kill-switch for the v4 AI chat overhaul (Suggested Blocks UX, Plan My Day
   * chat bubbles) — matches lifestack-calendar's FF_lifestack_v4. Web has no
   * surviving "v3" UI to fall back to (the old modal/panel were replaced, not
   * kept side-by-side), so this only gates whether the v4 entry points run;
   * it doesn't switch between two full UI implementations. */
  FF_lifestack_v4?: boolean;
  /** Kept on the remote-config payload for compatibility. Chat and Plan My
   * Day always use the science v5 streams; nothing in the app reads this. */
  FF_ai_v5?: boolean;
};

export const defaultRemoteConfig: ExtendedFeatureFlags = {
  force_update_version: '1.0.0',
  FF_calendly: true,
  FF_30_day_trial: false,
  FF_ai_scheduler_chat_v5: true,
  FF_github_integrations: true,
  FF_ai_scheduler_adjust_v2: true,
  FF_notion_integ: true,
  FF_google_tasks: false,
  FF_lifestack_v3: true,
  FF_lifestack_v4: true,
  FF_ai_v5: true,
  team_plan_beta_users: {
    emails: [],
  },
};

const LOCAL_FEATURE_FLAG_OVERRIDES_KEY = 'lifestack_feature_flag_overrides';

const getLocalFeatureFlagOverrides = (): Partial<ExtendedFeatureFlags> => {
  if (!isDevelopment) {
    return {};
  }

  try {
    const rawOverrides = window.localStorage.getItem(LOCAL_FEATURE_FLAG_OVERRIDES_KEY);
    if (!rawOverrides) {
      return {};
    }

    const parsedOverrides = JSON.parse(rawOverrides);
    if (!parsedOverrides || typeof parsedOverrides !== 'object' || Array.isArray(parsedOverrides)) {
      return {};
    }

    return Object.keys(defaultRemoteConfig).reduce<Partial<ExtendedFeatureFlags>>(
      (overrides, key) => {
        const featureFlagKey = key as keyof ExtendedFeatureFlags;
        if (featureFlagKey in parsedOverrides) {
          overrides[featureFlagKey] = parsedOverrides[featureFlagKey];
        }
        return overrides;
      },
      {}
    );
  } catch {
    return {};
  }
};

export const isRemoteConfigStorageError = (error: unknown): boolean => {
  const message = String(error || '').toLowerCase();
  return (
    message.includes('remoteconfig/storage-open') ||
    message.includes('remoteconfig/storage-get') ||
    message.includes('remoteconfig/indexed-db-unavailable') ||
    message.includes('indexed db is not supported') ||
    message.includes('indexed database server')
  );
};

export const parseTeamPlanBetaUsers = (value?: string): FeatureFlags['team_plan_beta_users'] => {
  if (!value) {
    return { emails: [] };
  }

  try {
    const parsed = JSON.parse(value) as FeatureFlags['team_plan_beta_users'];
    if (Array.isArray(parsed?.emails)) {
      return parsed;
    }
  } catch {
    // fall through to defaults
  }

  return { emails: [] };
};

export const parseAlphaUsers = (value?: string): FeatureFlags['alpha_users'] => {
  if (!value) {
    return { user_emails: [], updated_at: '' };
  }

  try {
    const parsed = JSON.parse(value) as FeatureFlags['alpha_users'];
    if (Array.isArray(parsed?.user_emails)) {
      return parsed;
    }
  } catch {
    // fall through to defaults
  }

  return { user_emails: [], updated_at: '' };
};

export const useFeatureFlag = () => {
  const [featureFlags, setFeatureFlags] = useState<ExtendedFeatureFlags>(defaultRemoteConfig);
  const [isFeatureFlagsLoading, setIsFeatureFlagsLoading] = useState<boolean>(true);
  const { user } = useAuthContext();

  useEffect(() => {
    const init = async () => {
      if (isDevelopment) {
        // Use customized local feature flags for development
        setFeatureFlags({
          ...defaultRemoteConfig,
          ...getLocalFeatureFlagOverrides(),
        });
        setIsFeatureFlagsLoading(false);
        return;
      }

      const remoteConfigSupported = await isSupported().catch(() => false);
      if (!remoteConfigSupported) {
        setFeatureFlags(defaultRemoteConfig);
        setIsFeatureFlagsLoading(false);
        return;
      }

      try {
        remoteConfig.settings = {
          minimumFetchIntervalMillis: 3600000, // 1 hour
          fetchTimeoutMillis: 60000, // 1 minute
        };

        await fetchAndActivate(remoteConfig);
        remoteConfig.defaultConfig = defaultRemoteConfig;

        const ff = getAll(remoteConfig);
        const alphaUsers = parseAlphaUsers(ff.alpha_users?.asString?.());
        const isAlphaUser = user?.email && alphaUsers.user_emails.includes(user.email);
        const teamPlanBetaUsers = parseTeamPlanBetaUsers(ff.team_plan_beta_users?.asString?.());

        setFeatureFlags({
          FF_calendly: user.access === 'internal' ? true : ff.FF_calendly.asBoolean(),
          FF_30_day_trial: user.access === 'internal' ? true : ff.FF_30_day_trial.asBoolean(),
          force_update_version: ff.force_update_version.asString(),
          FF_ai_scheduler_chat_v5:
            user.access === 'internal' ? true : ff.FF_ai_scheduler_chat_v5.asBoolean(),
          FF_ai_scheduler_adjust_v2:
            user.access === 'internal' ? true : ff.FF_ai_scheduler_adjust_v2.asBoolean(),
          FF_github_integrations:
            user.access === 'internal' ? true : ff.FF_github_integrations.asBoolean(),
          FF_notion_integ:
            user.access === 'internal'
              ? true
              : (ff.FF_notion_integ?.asBoolean?.() ?? defaultRemoteConfig.FF_notion_integ ?? false),
          FF_google_tasks:
            user.access === 'internal'
              ? true
              : (ff.FF_google_tasks?.asBoolean?.() ?? defaultRemoteConfig.FF_google_tasks ?? false),

          FF_lifestack_v3: ff.FF_lifestack_v3.asBoolean(),
          FF_lifestack_v4:
            user.access === 'internal'
              ? true
              : (ff.FF_lifestack_v4?.asBoolean?.() ?? defaultRemoteConfig.FF_lifestack_v4),
          FF_ai_v5:
            user.access === 'internal'
              ? true
              : (ff.FF_ai_v5?.asBoolean?.() ?? defaultRemoteConfig.FF_ai_v5),
          team_plan_beta_users: teamPlanBetaUsers,
        });
      } catch (error) {
        if (isRemoteConfigStorageError(error)) {
          console.info('Remote config storage unavailable, using default feature flags.');
        } else {
          console.warn('Failed to fetch remote config, using default feature flags:', error);
        }
        // Keep using defaultRemoteConfig when remote config fails
      } finally {
        setIsFeatureFlagsLoading(false);
      }
    };

    init();
  }, [user.email]);

  const isTeamPlanUser =
    user.access === 'internal' ||
    Boolean(user?.email && featureFlags.team_plan_beta_users?.emails?.includes(user.email));

  return { featureFlags, isFeatureFlagsLoading, isTeamPlanUser };
};
